import { z } from 'zod';
import { EncryptedArtifactRef } from './artifact-ref.js';
import { PrivateEntityId } from './encrypted-entities.js';
import { PrivateProjectBinding } from './private-project.js';
/**
 * Barked items (docs/ux/project-tab-spec.md §3.2). One `bark` entity is one item (= one
 * segment of a conversation). Everything below is sealed inside the encrypted entity; the hub
 * only ever sees an opaque entity head and, for routing, the command envelope's machine id.
 *
 * Writers: the device appends user messages, confirmations and manual splits; the triage daemon
 * appends agent messages, proposals, actions, splits and summaries. Every array is
 * append-or-update by `id`, so concurrent writers merge (`mergeBark`).
 */
const Id = PrivateEntityId;
/** Message/proposal/action ids: client- or daemon-minted, opaque, bounded. */
const LocalId = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9:_-]+$/);
const At = z.number().int().nonnegative();
const RunId = z.string().min(1).max(256);
export const BARK_TEXT_MAX = 20_000;
export const BARK_THREAD_MAX = 500;
export const BARK_HANDOFF_MAX = 1200;
/** Undo is deterministic only inside this window (and before any affected run/landing). */
export const BARK_UNDO_WINDOW_MS = 10 * 60_000;
/** Bark attachments: sealed artifacts, same shape as `run.message` attachments. */
export const BarkAttachment = z.strictObject({
    encrypted: EncryptedArtifactRef.optional(),
    artifactId: z.string().min(1).max(256),
    name: z.string().max(512).optional(),
    mime: z.string().max(255).optional(),
});
export const BarkMode = z.enum(['quick', 'discuss']);
export const BarkStatus = z.enum([
    'queued',
    'triaging',
    'discussing',
    'handled',
    'failed',
    'undone',
    'cancelled',
]);
export const BarkSource = z.enum([
    'tab',
    'capture',
    'notification',
    'cli',
    'direct',
    'split',
    // phase2-decisions: a decision thread whose asking run ended (handed to triage)
    'decision',
]);
export const BarkThreadMessage = z.strictObject({
    id: LocalId,
    at: At,
    author: z.enum(['user', 'agent', 'system']),
    text: z.string().max(BARK_TEXT_MAX),
    attachments: z.array(BarkAttachment).max(20).optional(),
    /** Agent message carrying a proposal card. */
    proposalId: LocalId.optional(),
    /** Manual "this belongs to…" moves. */
    movedFrom: Id.optional(),
    movedTo: Id.optional(),
    segmentChip: z.enum(['suggest-split', 'split-done', 'continue-or-new']).optional(),
});
/**
 * A typed action inside a proposal. Same shapes as the direct triage tools, so executing a
 * confirmed proposal replays exactly what quick mode would have done.
 */
export const ProposedOp = z.discriminatedUnion('op', [
    z.strictObject({
        op: z.literal('task.create'),
        title: z.string().trim().min(1).max(200),
        description: z.string().max(BARK_TEXT_MAX).optional(),
        workstreamId: Id.optional(),
        acceptance: z.string().max(4000).optional(),
        autoLand: z.boolean().optional(),
    }),
    z.strictObject({
        op: z.literal('run.steer'),
        runId: RunId,
        text: z.string().min(1).max(BARK_TEXT_MAX),
    }),
    z.strictObject({ op: z.literal('answer.save'), text: z.string().min(1).max(BARK_TEXT_MAX) }),
    z.strictObject({ op: z.literal('park'), note: z.string().max(2000).optional() }),
    z.strictObject({
        op: z.literal('task.priority'),
        taskId: Id,
        priority: z.number().int().min(0).max(10),
    }),
]);
export const BarkProposalKind = z.enum([
    'tasks',
    'workstream',
    'steer',
    'answer',
    'park',
    'priority',
    'mixed',
]);
export const BarkProposal = z.strictObject({
    id: LocalId,
    at: At,
    kind: BarkProposalKind,
    title: z.string().min(1).max(200),
    actions: z.array(ProposedOp).min(1).max(20),
    note: z.string().max(2000).optional(),
    status: z.enum(['open', 'confirmed', 'adjusted', 'declined', 'superseded', 'expired']),
    confirmedAt: At.optional(),
    confirmedBy: z.string().max(256).optional(),
});
// ---- bark-route: routing constraints on a bark, and the routing a run actually used ----
/** Every field absent = Auto (Viko picks). A set field is a binding constraint (constraints > learned > defaults). */
export const BarkRouting = z.strictObject({
    agent: z.enum(['claude', 'codex']).optional(),
    machineId: z.string().min(1).max(256).optional(),
    accountId: z.string().min(1).max(160).optional(),
    model: z.string().min(1).max(160).optional(),
});
/** What a started run used and why: `constraint` = the user's choice, `auto` = Viko's pick. */
export const BarkRoutingUsed = BarkRouting.extend({
    source: z.enum(['constraint', 'auto']),
    note: z.string().max(300).optional(),
});
/** Entity value of the `barkRoute` (global) and `barkRouteProject` settings; sealed like any setting. */
export const BarkRouteSetting = z.strictObject({
    routing: BarkRouting,
    projectId: Id.optional(),
});
/** True when no field is set (Auto everywhere). */
export const isAutoRouting = (r) => !r || (!r.agent && !r.machineId && !r.accountId && !r.model);
// ---- end bark-route ----
export const BarkTarget = z.strictObject({
    type: z.enum(['task', 'run', 'workstream', 'decision', 'rule', 'goal']),
    id: z.string().min(1).max(256),
    role: z.enum(['created', 'updated', 'steered', 'linked']).optional(),
    /** bark-route: for a started run, the routing it used (shown in the outcome tree). */
    routing: BarkRoutingUsed.optional(),
});
/** Recorded operations, inverted deterministically by Undo (§2.6). */
export const BarkOp = z.discriminatedUnion('op', [
    z.strictObject({ op: z.literal('task.created'), taskId: Id }),
    z.strictObject({
        op: z.literal('task.updated'),
        taskId: Id,
        before: z.strictObject({
            status: z.string().max(32).optional(),
            priority: z.number().int().min(0).max(10).optional(),
            // triage-tools: moves and reorders are undoable too
            workstreamId: Id.nullable().optional(),
            rank: z.number().finite().optional(),
        }),
    }),
    z.strictObject({
        op: z.literal('run.steered'),
        runId: RunId,
        messageId: LocalId,
    }),
    z.strictObject({ op: z.literal('run.started'), runId: RunId, taskId: Id.optional() }),
    z.strictObject({ op: z.literal('workstream.created'), workstreamId: Id }),
    z.strictObject({ op: z.literal('decision.created'), decisionId: z.string().min(1).max(256) }),
]);
export const BarkDecision = z.enum([
    'answered',
    'steered',
    'tasks',
    'merged',
    'parked',
    'asked',
    'noted',
    'started',
    'direct',
]);
export const BarkSourceRef = z.strictObject({
    kind: z.string().min(1).max(32),
    ref: z.string().min(1).max(1000),
    label: z.string().max(200),
});
export const BarkAction = z.strictObject({
    id: LocalId,
    /** Absent for quick-mode direct handling. */
    proposalId: LocalId.optional(),
    decision: BarkDecision,
    answer: z
        .strictObject({
        text: z.string().max(BARK_TEXT_MAX),
        sources: z.array(BarkSourceRef).max(30),
    })
        .optional(),
    targets: z.array(BarkTarget).max(50),
    assumptions: z
        .array(z.strictObject({
        text: z.string().min(1).max(2000),
        reversible: z.boolean(),
        taskId: Id.optional(),
    }))
        .max(20),
    ops: z.array(BarkOp).max(100),
    status: z.enum(['active', 'undone', 'cancelled']),
    at: At,
});
export const BarkHistoryKind = z.enum([
    'created',
    'triaged',
    'proposal',
    'confirmed',
    'action',
    'redirect',
    'undo',
    'retriage',
    'context',
    'cancel',
    'split',
    'merge',
    'move',
    'failed',
]);
export const BarkHistoryEntry = z.strictObject({
    at: At,
    kind: BarkHistoryKind,
    by: z.enum(['user', 'agent', 'system']),
    note: z.string().max(2000).optional(),
});
export const BarkRecord = z.strictObject({
    id: Id,
    projectId: Id,
    mode: BarkMode,
    /** The first user message, verbatim (the dictation transcript when dictated). */
    text: z.string().max(BARK_TEXT_MAX),
    dictated: z.boolean(),
    attachments: z.array(BarkAttachment).max(20),
    hints: z.strictObject({
        workstreamId: Id.optional(),
        taskId: Id.optional(),
        runId: RunId.optional(),
    }),
    source: BarkSource,
    /** bark-route: user routing constraints for this item (absent = Auto). Sealed with the item. */
    routing: BarkRouting.optional(),
    /** Idempotency key minted by the device (offline queue). */
    clientId: z.string().min(1).max(128),
    title: z.string().max(120).optional(),
    status: BarkStatus,
    triage: z.strictObject({
        routerRunId: RunId.optional(),
        itemRunId: RunId.optional(),
        nativeSessionId: z.string().max(256).optional(),
        machineId: z.string().max(256).optional(),
        startedAt: At.optional(),
        attempts: z.number().int().nonnegative().max(1000),
    }),
    splitFrom: z
        .strictObject({
        itemId: Id,
        atMessageId: LocalId,
        handoff: z.string().max(BARK_HANDOFF_MAX),
        by: z.enum(['user', 'agent']),
        confidence: z.enum(['high', 'medium']).optional(),
    })
        .optional(),
    splitsTo: z.array(Id).max(50),
    topicSummary: z.string().max(1000).optional(),
    thread: z.array(BarkThreadMessage).max(BARK_THREAD_MAX),
    proposals: z.array(BarkProposal).max(100),
    interpretation: z.string().max(2000).optional(),
    actions: z.array(BarkAction).max(50),
    history: z.array(BarkHistoryEntry).max(500),
    createdAt: At,
    updatedAt: At,
});
/** A fresh bark as the device writes it (the first thread message carries the verbatim text). */
export function newBark(i) {
    return BarkRecord.parse({
        id: i.id,
        projectId: i.projectId,
        mode: i.mode,
        text: i.text,
        dictated: i.dictated ?? false,
        attachments: i.attachments ?? [],
        hints: i.hints ?? {},
        ...(i.routing && !isAutoRouting(i.routing) ? { routing: i.routing } : {}),
        source: i.source ?? 'tab',
        clientId: i.clientId,
        status: 'queued',
        triage: { attempts: 0 },
        ...(i.splitFrom ? { splitFrom: i.splitFrom } : {}),
        splitsTo: [],
        thread: [
            {
                id: i.messageId,
                at: i.now,
                author: 'user',
                text: i.text,
                ...(i.attachments?.length ? { attachments: i.attachments } : {}),
            },
        ],
        proposals: [],
        actions: [],
        history: [{ at: i.now, kind: 'created', by: 'user' }],
        createdAt: i.now,
        updatedAt: i.now,
    });
}
/** Persisted body: the opaque id lives in the authenticated entity context, not the value. */
export function barkBody(b) {
    const { id: _id, ...rest } = BarkRecord.parse(b);
    return rest;
}
/** Hydrate from an authenticated entity row (the context id wins; a mismatching body is refused). */
export function hydrateBark(row) {
    if (row.value.id !== undefined && row.value.id !== row.id)
        throw new Error('bark identity mismatch');
    return BarkRecord.parse({ ...row.value, id: row.id });
}
function upsert(base, next) {
    const out = [...base];
    for (const item of next) {
        const i = out.findIndex((x) => x.id === item.id);
        if (i < 0)
            out.push(item);
        else
            out[i] = item;
    }
    return out;
}
const STATUS_RANK = {
    open: 0,
    superseded: 1,
    expired: 1,
    declined: 2,
    confirmed: 3,
    adjusted: 3,
    active: 0,
    undone: 2,
    cancelled: 2,
};
/** Terminal states never regress (a stale writer cannot reopen a confirmed proposal). */
function upsertMonotonic(base, next) {
    const out = [...base];
    for (const item of next) {
        const i = out.findIndex((x) => x.id === item.id);
        if (i < 0)
            out.push(item);
        else if ((STATUS_RANK[item.status] ?? 0) >= (STATUS_RANK[out[i].status] ?? 0))
            out[i] = item;
    }
    return out;
}
/**
 * Merge a concurrent writer's view into the latest record: arrays append-or-update by id,
 * scalars from `mine` (the writer that is committing), terminal states never regress.
 */
export function mergeBark(latest, mine) {
    const history = [...latest.history];
    for (const h of mine.history)
        if (!history.some((x) => x.at === h.at && x.kind === h.kind && x.note === h.note))
            history.push(h);
    history.sort((a, b) => a.at - b.at);
    const thread = upsert(latest.thread, mine.thread).sort((a, b) => a.at - b.at);
    return BarkRecord.parse({
        ...latest,
        ...mine,
        thread: thread.slice(-BARK_THREAD_MAX),
        proposals: upsertMonotonic(latest.proposals, mine.proposals).slice(-100),
        actions: upsertMonotonic(latest.actions, mine.actions).slice(-50),
        splitsTo: [...new Set([...latest.splitsTo, ...mine.splitsTo])].slice(-50),
        history: history.slice(-500),
        triage: {
            ...latest.triage,
            ...mine.triage,
            attempts: Math.max(latest.triage.attempts, mine.triage.attempts),
        },
        updatedAt: Math.max(latest.updatedAt, mine.updatedAt),
    });
}
/** Stable input for the proposal digest the device signs on confirm (daemon re-checks it). */
export function proposalDigestInput(p) {
    return { v: 1, id: p.id, kind: p.kind, title: p.title, actions: p.actions };
}
export const PROPOSAL_DIGEST_DOMAIN = 'viko/bark-proposal/v1';
/** Explicit topic-change markers (§1.3a signal 1). High confidence split in P1. */
const SPLIT_MARKERS = [
    /^\/new\b/i,
    /^new topic\b/i,
    /\b(also|and),?\s+(another|separate|unrelated)\s+(thing|topic|question)\b/i,
    /^(separately|unrelated)\b[:,]?/i,
    /\balso,?\s+unrelated\b/i,
];
export function hasExplicitSplitMarker(text) {
    const t = text.trim();
    return SPLIT_MARKERS.some((re) => re.test(t));
}
/**
 * Hub-visible projection (routing, badges, push): ids, bounded enums, counters and timestamps
 * only. Never text. Not stored by P1 (commands are machine-addressed); defined so the allowlist
 * is tested now.
 */
export const BarkProjection = z.strictObject({
    id: Id,
    projectId: Id,
    status: BarkStatus,
    needsYou: z.boolean(),
    openProposals: z.number().int().nonnegative().max(100),
    createdAt: At,
    updatedAt: At,
});
export function barkProjection(b, needsYou = false) {
    return BarkProjection.parse({
        id: b.id,
        projectId: b.projectId,
        status: b.status,
        needsYou,
        openProposals: b.proposals.filter((p) => p.status === 'open').length,
        createdAt: b.createdAt,
        updatedAt: b.updatedAt,
    });
}
// --- commands (additive; spread into COMMAND_PAYLOADS) --------------------------------------------
export const BarkControlAction = z.enum([
    'message',
    'confirm',
    'adjust',
    'decline',
    'undo',
    'redirect',
    'retriage',
    'cancel',
    'split',
    'move',
    'merge',
    // segmentation: background scope check of a direct steer (daemon-side, no model call)
    'scope-check',
]);
/**
 * `bark.submit` / `bark.control`: device-signed, non-risky, sealed. Content lives in the bark
 * entity; the command carries ids plus, for confirm/adjust, the digest of exactly what the user
 * saw so the daemon executes nothing else.
 */
export const BARK_COMMAND_PAYLOADS = {
    'bark.submit': z
        .object({
        barkId: Id,
        projectId: Id,
        /** Same signed binding as `run.start`: lets the triage machine find its checkout. */
        privateProject: PrivateProjectBinding.optional(),
    })
        .refine((p) => !p.privateProject || p.privateProject.snapshot.id === p.projectId, 'private project mismatch'),
    'bark.control': z.object({
        barkId: Id,
        projectId: Id,
        action: BarkControlAction,
        messageId: LocalId.optional(),
        proposalId: LocalId.optional(),
        /** base64url sha-256 of `proposalDigestInput(proposal as shown)`. */
        proposalDigest: z.string().max(64).optional(),
        /** `adjust`: the edited actions (their digest is `proposalDigest`). */
        adjusted: z.array(ProposedOp).max(20).optional(),
        targetItemId: Id.optional(),
        instruction: z.string().max(4000).optional(),
        // ---- segmentation: "This belongs to…" (move) options ----
        /** Also move every later message. */
        withLater: z.boolean().optional(),
        /** Also move the actions whose proposals sit in the moved messages. */
        withActions: z.boolean().optional(),
        // ---- end segmentation ----
    }),
};
// --- MCP triage tools (additive; spread into VIKO_MCP_TOOLS) --------------------------------------
export const VikoBarkReportInput = z.object({
    barkId: Id,
    interpretation: z.string().min(1).max(2000),
    /** Short topic title for the item (≤120). */
    title: z.string().max(120).optional(),
    topicSummary: z.string().max(1000).optional(),
    action: z.object({
        /** Idempotency key per (barkId, actionId); a repeat updates the same branch. */
        actionId: LocalId.optional(),
        proposalId: LocalId.optional(),
        decision: BarkDecision.exclude(['direct']),
        targets: z.array(BarkTarget).max(50).optional(),
        assumptions: z
            .array(z.object({ text: z.string().min(1).max(2000), reversible: z.boolean() }))
            .max(20)
            .optional(),
        answer: z.string().max(BARK_TEXT_MAX).optional(),
        sources: z.array(BarkSourceRef).max(30).optional(),
    }),
    /** One clarifying question instead of acting (quick mode may choose to discuss). */
    question: z.string().max(4000).optional(),
});
export const VikoBarkReportOutput = z.object({
    ok: z.boolean(),
    actionId: LocalId,
    status: BarkStatus,
});
export const VikoRunSteerInput = z.object({
    runId: RunId,
    barkId: Id,
    /** The user's words, verbatim. */
    text: z.string().min(1).max(BARK_TEXT_MAX),
    /** One line of context for the run. */
    context: z.string().max(500).optional(),
});
export const VikoRunSteerOutput = z.object({
    delivered: z.boolean(),
    messageId: LocalId,
    status: z.enum([
        'delivered',
        'queued',
        'not_live',
        // triage-tools: refused — the message is about other work (create a task or item instead)
        'out_of_scope',
    ]),
    reason: z.string().max(500).optional(),
});
export const VikoProposeInput = z.object({
    itemId: Id,
    kind: BarkProposalKind,
    title: z.string().min(1).max(200),
    actions: z.array(ProposedOp).min(1).max(20),
    note: z.string().max(2000).optional(),
    /** The message shown above the card in the thread. */
    message: z.string().max(4000).optional(),
});
export const VikoProposeOutput = z.object({ proposalId: LocalId, status: z.literal('open') });
export const VikoItemSplitInput = z.object({
    itemId: Id,
    fromMessageId: LocalId,
    confidence: z.enum(['high', 'medium']),
    title: z.string().min(1).max(120),
    handoff: z.string().min(1).max(BARK_HANDOFF_MAX),
});
export const VikoItemSplitOutput = z.object({
    status: z.enum(['split', 'suggested']),
    newItemId: Id.optional(),
});
export const BARK_MCP_TOOLS = {
    viko_bark_report: { input: VikoBarkReportInput, output: VikoBarkReportOutput },
    viko_run_steer: { input: VikoRunSteerInput, output: VikoRunSteerOutput },
    viko_propose: { input: VikoProposeInput, output: VikoProposeOutput },
    viko_item_split: { input: VikoItemSplitInput, output: VikoItemSplitOutput },
};
/** Tools only a `role: 'triage'` run may list or call. */
export const TRIAGE_ONLY_TOOLS = Object.keys(BARK_MCP_TOOLS);
//# sourceMappingURL=bark.js.map